import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  Stethoscope, Dna, ArrowRight, ShieldCheck,
  CheckCircle2, Award,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1758691461935-202e2ef6b69f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBmYW1pbHklMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzg0NDU3MzkzfDA&ixlib=rb-4.1.0&q=85&w=1400";
const DNA_IMG = "https://images.unsplash.com/photo-1633167606207-d840b5070fc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxkbmElMjBnZW5ldGljJTIwdGVzdGluZ3xlbnwwfHx8fDE3ODQ0NTczOTN8MA&ixlib=rb-4.1.0&q=85&w=1200";

const FEATURES = [
  { icon: Stethoscope, title: "Onboard Physicians", body: "Bring GeneID genetic testing into physician offices across the country." },
  { icon: Dna, title: "Advanced Testing", body: "Give patients access to CLIA-certified GeneID hereditary and molecular testing." },
  { icon: Award, title: "Personalized Medicine", body: "Help physicians tailor care with precise, actionable molecular insights." },
];

function Overline({ children }) {
  return <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot mb-4">{children}</p>;
}

export default function Landing() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);

  useEffect(() => {
    api.get("/tests").then(({ data }) => setTests(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0 stripes-bg opacity-60" />
        <div className="absolute inset-0 afha-grain" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 afha-rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide mb-7">
              <ShieldCheck className="h-4 w-4 text-patriot-light" />
              American Family Health Association
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Bring Advanced Genetic Testing to more families.
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
              Join AFHA as a representative — Onboarding Physicians.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/signup")} data-testid="hero-join-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 text-base gap-2">
                Become a Rep <ArrowRight className="h-4 w-4" />
              </Button>
              <Button onClick={() => navigate("/login")} data-testid="hero-login-btn" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-navy h-12 px-8 text-base">
                Log In
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 afha-rise" style={{ animationDelay: "0.15s" }}>
            <div className="relative" data-testid="hero-video">
              <div className="absolute -inset-3 border border-white/15 rounded-2xl" />
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl bg-black" style={{ paddingTop: "56.25%" }}>
                <iframe
                  title="AFHA video"
                  src="https://player.vimeo.com/video/1207441045?h=b8314ea035&autoplay=1&muted=1&playsinline=1"
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 scroll-mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Our Mission</Overline>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy leading-tight">
              A National Marketing Organization for GeneID
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              We are recruiting smart, knowledgeable representatives to onboard
              physicians to provide genetic testing in their offices. If you have
              working relationships with physicians, this is the opportunity for you.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group bg-white border border-border rounded-xl p-6 hover:border-navy hover:shadow-[0_8px_30px_rgba(15,23,42,0.08)] transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-lg bg-navy/5 flex items-center justify-center mb-5 group-hover:bg-navy transition-colors duration-300">
                  <f.icon className="h-6 w-6 text-navy group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT GENEID */}
      <section id="about-geneid" className="bg-white border-y border-border scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <img src={DNA_IMG} alt="Genetic testing DNA" className="rounded-xl w-full h-[420px] object-cover" />
              <div className="absolute -top-5 -right-5 bg-navy text-white rounded-lg px-5 py-4 shadow-xl hidden sm:flex items-center gap-3">
                <Award className="h-6 w-6 text-patriot-light" />
                <div className="text-sm leading-tight"><span className="font-bold">CLIA Certified</span><br /><span className="text-white/70 text-xs">CAP Accredited Lab</span></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Overline>About Gene ID</Overline>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy leading-tight">
              A leader in Personalized Medicine
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              As a leading company in Personalized Medicine, GeneID assists in the
              screening, diagnosis, and classification of cancer patients, and the
              optimization of drug therapy. A CLIA certified and CAP accredited
              molecular laboratory, GeneID provides the highest quality molecular
              testing services to medical practitioners nationwide.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              GeneID translates scientific discoveries into customized, more effective
              treatments — helping predict disease risk and determine how patients
              might respond to certain therapies.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {["Expert pathologists & geneticists", "Comprehensive molecular testing", "Nationwide practitioner network", "Precision drug-to-patient matching"].map((t) => (
                <div key={t} className="flex items-start gap-2.5 text-sm text-navy">
                  <CheckCircle2 className="h-5 w-5 text-patriot shrink-0 mt-0.5" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTS */}
      <section id="tests" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 scroll-mt-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <Overline>GeneID Tests</Overline>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy leading-tight">
              Comprehensive genetic testing
            </h2>
          </div>
          <Dna className="h-12 w-12 text-navy/15 hidden sm:block" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tests.map((t, i) => (
            <button
              key={t.slug}
              onClick={() => navigate(`/tests/${t.slug}`)}
              data-testid={`test-card-${t.slug}`}
              className="group text-left bg-white border border-border rounded-xl p-7 hover:border-patriot hover:shadow-[0_10px_40px_rgba(15,23,42,0.09)] transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold tracking-wide uppercase text-patriot bg-patriot/8 rounded-full px-3 py-1">{t.category}</span>
                <span className="font-mono text-xs text-muted-foreground">{t.gene_count}</span>
              </div>
              <h3 className="font-display text-2xl text-navy mb-2">{t.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">{t.short}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-patriot transition-colors">
                View details <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy">Ready to get started?</h2>
          <p className="mt-4 text-muted-foreground">Become an AFHA representative today.</p>
          <Button onClick={() => navigate("/signup")} data-testid="cta-join-btn" className="mt-8 rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-10 text-base gap-2">
            Become a Rep <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* PHYSICIAN ONBOARDING */}
      <section id="onboarding" className="bg-white border-y border-border scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <div className="max-w-2xl">
            <Overline>Physician Onboarding</Overline>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-navy leading-tight">
              Bring GeneID testing into your practice
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              AFHA makes it simple for physicians to offer advanced genetic testing to their
              patients — no lab equipment or upfront cost. Our representatives handle the setup
              so you can focus on care.
            </p>
          </div>

          <div className="mt-12 bg-navy-deep text-white rounded-xl p-8 afha-grain flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl">Ready to offer GeneID testing?</h3>
              <p className="text-white/70 mt-1 text-sm">See the full four-step process and start a secure enrollment form.</p>
            </div>
            <Button
              onClick={() => navigate("/physician-onboarding")}
              data-testid="onboarding-cta"
              className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2 shrink-0"
            >
              Learn about onboarding <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
